import { DateTime } from "luxon";
import { PathPickSmartStoreInfoResponse } from "@/features/pathpick/features/basket/types/response";
import { PathPickSmartStoreInfoRequestBody } from "@/features/pathpick/features/basket/types/request-body";
import {
  PathPickBasketSelection,
  PathPickBasketSoldOutFilter,
  PathPickBasketTab,
  PathPickNaverPartnerAddressBook,
  PathPickNaverPartnerAddressType,
} from "@/features/pathpick/features/basket/types";
import { PATH_PICK_OVERSEAS_RELEASE_POSTAL_CODE } from "@/features/pathpick/features/basket/constants";
import { PathPickProductRequestParams } from "@/features/pathpick/types/request-params";
import {
  PathPickMutationProduct,
  PathPickProductChannelStatus,
  PathPickProductKey,
  PathPickProductRequestKind,
  PathPickProductType,
} from "@/features/pathpick/types";
import { getPathPickAllRequestKinds } from "@/features/pathpick/constants";
import { getPathPickRequestSlot } from "@/features/pathpick/utils";

// 탭별 상품 조회 파라미터 — 목록 조회와 탭 카운트 조회가 함께 쓰는 단일 소스.
// request_type__in은 종류별로 독립 판정한 합집합이라 "연동 또는 파일 생성"을 한 요청으로 표현한다.
// syncKinds — 권한 있는 마켓(usePathPickSyncKinds). 네이버 외 마켓 연동 이력도 등록 신청함에 포함한다.
export const getPathPickBasketTabParams = (
  tab: PathPickBasketTab,
  syncKinds: PathPickProductRequestKind[]
): PathPickProductRequestParams => {
  switch (tab) {
    // 등록 신청함 = 3종 중 하나라도 신청 이력이 있는 상품
    case "requested":
      return { requestType__in: getPathPickAllRequestKinds(syncKinds) };
    case "ordered":
      return { isBuyerOrdered: true };
    // 작업 전 = 신청 이력 없음(NONE) + 주문 안 함
    case "pending":
    default:
      return {
        requestType__in: ["NONE"],
        isBuyerOrdered: false,
      };
  }
};

// 등록 채널 필터 — 등록 신청한 탭에서만, 선택이 있을 때만 탭 기본값(3종 전체)을 그 채널로 좁힌다.
export const getPathPickBasketRequestChannelParams = (
  tab: PathPickBasketTab,
  channels: PathPickProductRequestKind[] | undefined
): PathPickProductRequestParams =>
  tab === "requested" && channels?.length ? { requestType__in: channels } : {};

// 품절 상품 필터 → 조회 파라미터. 모든 탭에 노출되는 단일 선택 필터다.
// 전체(undefined)는 파라미터를 아예 빼는데, 담은 상품 조회(is_in_basket=true)는 is_sold_out을
// 생략하면 서버가 품절 상품도 함께 내려주므로 `전체`의 의미와 정확히 맞는다.
export const getPathPickBasketSoldOutParams = (
  soldOut: PathPickBasketSoldOutFilter | undefined
): PathPickProductRequestParams =>
  soldOut ? { isSoldOut: soldOut === "ONLY" } : {};

// 상품 유형 필터 → 조회 파라미터. 작업 전 탭에서만, 핫딜 권한이 있을 때만 노출되는 필터다.
//
// 체크박스 UI지만 product_type은 단일 값 파라미터라 OR을 표현할 수 없다.
// 정확히 하나만 선택했을 때만 전달하고, 둘 다/미선택은 전체 조회(파라미터 없음)와 동일하게 둔다.
export const getPathPickBasketProductTypeParams = (
  tab: PathPickBasketTab,
  productTypes: PathPickProductType[] | undefined,
  showHotDeal: boolean
): PathPickProductRequestParams => {
  if (tab !== "pending" || !showHotDeal || productTypes?.length !== 1) {
    return {};
  }

  return { productType: productTypes[0] };
};

// 해외 출고지로 인정하는 네이버 주소 종류 — 바이어가 "일반"으로 등록하는 경우가 있어 함께 인정한다.
// 조회(getNaverPartnerAddressBooks)의 type__in과 같은 집합을 유지해야 판정이 어긋나지 않는다.
const OVERSEAS_RELEASE_ADDRESS_TYPES: PathPickNaverPartnerAddressType[] = [
  "RELEASE",
  "GENERAL",
];

// 재신청을 막는 채널 상태 — 완료 여부는 여기가 아니라 isCompleted로 판정한다.
// PENDING/PROCESSING은 아직 처리 중인 건을 다시 접수하면 사용량만 이중 차감되므로 막는다.
// SUCCESS는 막지 않는다 — 마켓에서 삭제·연동 해제된 상품은 status가 SUCCESS로 남은 채
// isCompleted: false로 내려오므로, status로 막으면 다시 신청할 수 없게 된다.
// 목록이 아니라 맵으로 두는 이유 — 목록이면 새 상태가 빠졌을 때 조용히 "재신청 허용"으로 기울지만,
// 맵이면 컴파일 에러가 나 허용/차단을 정하고 지나가게 된다.
const IS_REQUEST_BLOCKED: Record<PathPickProductChannelStatus, boolean> = {
  SUCCESS: false,
  PENDING: true,
  PROCESSING: true,
  FAILED: false,
};

// 해당 종류로 이 상품을 (재)신청할 수 있는지 — 완료(isCompleted)거나 처리 중(PENDING/PROCESSING)이면 막는다.
// 신청은 채널 단위라 상품 하나가 채널마다 다른 상태일 수 있다(스마트스토어 성공 + KREAM 실패 등).
export const isSelectionRequestable = (
  selection: PathPickBasketSelection,
  kind: PathPickProductRequestKind
) => {
  const slot = getPathPickRequestSlot(selection.requests, kind);
  // 완료를 status보다 먼저 본다 — 연동 슬롯은 BASIC 플랜/네이버 미연동이면 status가 없고
  // isCompleted만 내려오므로, status만 보면 이미 연동된 상품이 재신청 대상에 들어간다.
  if (slot?.isCompleted) return false;
  const status = slot?.status ?? null;
  return status === null || !IS_REQUEST_BLOCKED[status];
};

// 선택 중 주어진 종류들로 신청할 수 있는 상품만 추린다.
// 종류가 여러 개면 "하나라도 신청 가능"이면 포함한다 — 요청 body가 requestTypes와 products를
// 따로 받아 채널별로 다른 상품 집합을 표현할 수 없으므로, 이미 처리된 채널의 스킵은 서버에 맡긴다
// (KREAM 미매칭 상품이 KREAM 파일에서만 자동 제외되는 것과 같은 위임이다).
export const getRequestableSelections = (
  selectedProducts: Map<PathPickProductKey, PathPickBasketSelection>,
  kinds: PathPickProductRequestKind[]
): PathPickBasketSelection[] =>
  Array.from(selectedProducts.values()).filter((selection) =>
    kinds.some((kind) => isSelectionRequestable(selection, kind))
  );

// 위 결과를 요청 body의 products 형태로 — 신청 실행부가 쓴다.
export const getRequestableProducts = (
  selectedProducts: Map<PathPickProductKey, PathPickBasketSelection>,
  kinds: PathPickProductRequestKind[]
): PathPickMutationProduct[] =>
  getRequestableSelections(selectedProducts, kinds).map(({ id, type }) => ({
    id,
    type,
  }));

// 네이버 주소록에서 패스레이드 해외 출고지 조건을 만족하는 주소를 모두 찾는다.
// 조회를 주소 종류로 좁혀 받지만, 판정 기준을 코드에서도 읽을 수 있게 addressType을 함께 확인한다.
// 두 종류를 인정하므로 같은 주소가 출고지/일반으로 중복 등록돼 매칭이 2건 이상일 수 있다.
export const findOverseasReleaseAddresses = (
  addressBooks?: PathPickNaverPartnerAddressBook[] | null
) =>
  (addressBooks ?? []).filter(
    (address) =>
      OVERSEAS_RELEASE_ADDRESS_TYPES.includes(address.addressType) &&
      address.overseasAddress &&
      address.postalCode === PATH_PICK_OVERSEAS_RELEASE_POSTAL_CODE
  );

// 위 조건을 만족하는 주소 한 건. null이면 해외 출고지 미확인.
// 등록 여부 배지와 저장 시 출고지 지정처럼 대표 1건만 필요한 곳에서 쓴다 —
// 네이버 출고지 지정의 정석이 RELEASE라 중복 등록 시 RELEASE를 GENERAL보다 우선한다.
export const findOverseasReleaseAddress = (
  addressBooks?: PathPickNaverPartnerAddressBook[] | null
) => {
  const matched = findOverseasReleaseAddresses(addressBooks);
  return (
    matched.find((address) => address.addressType === "RELEASE") ??
    matched[0] ??
    null
  );
};

// 연동 신청 가능 조건 — 주소록에 해외 출고지가 있고, 그 주소가 출고지로도 지정돼 있어야 한다.
// (기본 정보 설정 모달의 확인 카드는 주소록 등록 여부만 보므로 판정이 다르다)
// 매칭이 여러 건일 수 있으므로 대표 1건과 비교하지 않고 "지정된 출고지가 매칭에 속하는지"로 본다 —
// 대표 1건과 비교하면 지정된 쪽이 우선순위에서 밀린 주소일 때 잘못 미충족으로 판정된다.
export const isOverseasReleaseAddressSelected = (
  addressBooks?: PathPickNaverPartnerAddressBook[] | null,
  smartStoreInfo?: PathPickSmartStoreInfoResponse | null
) => {
  const releaseAddressId = smartStoreInfo?.releaseAddressId;
  if (releaseAddressId === null || releaseAddressId === undefined) return false;
  return findOverseasReleaseAddresses(addressBooks).some(
    (address) => address.addressBookNo === releaseAddressId
  );
};

// 저장된 스마트스토어 기본 정보 → 전체 PUT 요청 바디.
//
// 수정은 전체 PUT이라 보내지 않은 필드는 빈 값으로 덮인다. 한 필드만 바꾸는 화면
// (시작 가이드의 배송비 템플릿)이 나머지 값을 잃지 않도록 저장된 값을 그대로 되돌려 보낸다.
// 미설정은 요청 규약대로 null로, id 계열은 요청에서 아예 빠지도록 undefined로 둔다.
export const toPathPickSmartStoreInfoRequestBody = (
  smartStoreInfo?: PathPickSmartStoreInfoResponse | null
): PathPickSmartStoreInfoRequestBody => ({
  afterServiceTelephoneNumber:
    smartStoreInfo?.afterServiceTelephoneNumber ?? null,
  afterServiceGuideContent: smartStoreInfo?.afterServiceGuideContent ?? null,
  deliveryType: smartStoreInfo?.deliveryType ?? null,
  deliveryCompany: smartStoreInfo?.deliveryCompany ?? null,
  deliveryTemplateCode: smartStoreInfo?.deliveryTemplateCode ?? null,
  returnDeliveryFee: smartStoreInfo?.returnDeliveryFee ?? null,
  exchangeDeliveryFee: smartStoreInfo?.exchangeDeliveryFee ?? null,
  salePriceRules: smartStoreInfo?.salePriceRules ?? null,
  isSalePriceSync: smartStoreInfo?.isSalePriceSync ?? undefined,
  releaseAddressId: smartStoreInfo?.releaseAddressId ?? undefined,
  refundAddressId: smartStoreInfo?.refundAddressId ?? undefined,
  topImageFileId: smartStoreInfo?.topImage?.id,
  bottomImageFileId: smartStoreInfo?.bottomImage?.id,
});

// A/S 정보 — API 연동과 파일 신청 양쪽이 공통으로 요구하는 최소 조건.
export const isAfterServiceInfoComplete = (
  smartStoreInfo?: PathPickSmartStoreInfoResponse | null
) =>
  Boolean(smartStoreInfo?.afterServiceTelephoneNumber?.trim()) &&
  Boolean(smartStoreInfo?.afterServiceGuideContent?.trim());

// 배송 정보(택배사·반품/교환 배송비) — 별도 섹션으로 분리돼 API 연동과 파일 신청 양쪽이
// 공통으로 요구한다. 모달의 canSave와 판정 기준이 같아야 한다.
export const isDeliveryInfoComplete = (
  smartStoreInfo?: PathPickSmartStoreInfoResponse | null
) =>
  Boolean(smartStoreInfo?.deliveryCompany) &&
  // 배송비는 0원도 유효하므로 값의 유무 대신 숫자 여부로 판정한다.
  typeof smartStoreInfo?.returnDeliveryFee === "number" &&
  typeof smartStoreInfo?.exchangeDeliveryFee === "number";

// 스마트스토어 파일 신청에 필요한 판매자 기본 정보가 모두 채워졌는지.
// "저장 여부"로 판정하지 않는 이유: 단독 진입(설정 버튼)은 부분 저장을 허용하므로
// 저장은 됐지만 A/S 정보가 빈 계정이 존재할 수 있다.
export const isFileRequestInfoComplete = (
  smartStoreInfo?: PathPickSmartStoreInfoResponse | null
) =>
  isAfterServiceInfoComplete(smartStoreInfo) &&
  isDeliveryInfoComplete(smartStoreInfo) &&
  Boolean(smartStoreInfo?.deliveryTemplateCode?.trim());

// 스마트스토어 파일 신청에 필요한데 아직 비어 있는 항목의 라벨 i18n 키 — 파일 신청 모달의
// "미설정: ..." 목록에 쓴다. 판정 기준은 isFileRequestInfoComplete와 반드시 같아야 한다
// (어긋나면 "미설정 없음"으로 보이는데 신청은 관문에 막히는 상태가 된다).
export const getMissingFileRequestInfoLabelKeys = (
  smartStoreInfo?: PathPickSmartStoreInfoResponse | null
) => {
  // 화면에 나열되는 순서 그대로 둔다.
  const fields: [isMissing: boolean, labelKey: string][] = [
    [
      !smartStoreInfo?.deliveryTemplateCode?.trim(),
      "path_pick:basket.file_request.missing_delivery_template_code",
    ],
    [
      !smartStoreInfo?.deliveryCompany,
      "path_pick:basket.file_request.missing_delivery_company",
    ],
    [
      typeof smartStoreInfo?.returnDeliveryFee !== "number",
      "path_pick:basket.file_request.missing_return_delivery_fee",
    ],
    [
      typeof smartStoreInfo?.exchangeDeliveryFee !== "number",
      "path_pick:basket.file_request.missing_exchange_delivery_fee",
    ],
    [
      !smartStoreInfo?.afterServiceTelephoneNumber?.trim(),
      "path_pick:basket.file_request.missing_contact",
    ],
    [
      !smartStoreInfo?.afterServiceGuideContent?.trim(),
      "path_pick:basket.file_request.missing_notice",
    ],
  ];

  return fields
    .filter(([isMissing]) => isMissing)
    .map(([, labelKey]) => labelKey);
};

// API 연동 신청에 필요한 판매자 기본 정보 — A/S 정보 + 배송 정보 + 해외 출고지 지정.
// 배송비 템플릿 "코드"만 스마트스토어 파일 신청 전용이라 조건에서 빠진다(모달에서도 감춘다).
// 스마트스토어 API 설정은 smartStoreInfo가 아니라 제휴 연결 상태(connected)로 판정하므로 여기 없다.
export const isApiSyncInfoComplete = (
  smartStoreInfo?: PathPickSmartStoreInfoResponse | null,
  addressBooks?: PathPickNaverPartnerAddressBook[] | null
) =>
  isAfterServiceInfoComplete(smartStoreInfo) &&
  isDeliveryInfoComplete(smartStoreInfo) &&
  isOverseasReleaseAddressSelected(addressBooks, smartStoreInfo);

// 조회 시각이 1분 이내면 "방금 전"으로 표시하기 위한 판별
const JUST_NOW_THRESHOLD_MS = 60 * 1000;

export const isJustCheckedAt = (updatedAt: number) =>
  Date.now() - updatedAt < JUST_NOW_THRESHOLD_MS;

// 조회 시각 → "2026. 07. 27. 오전 09:30"
export const formatLastCheckedAt = (updatedAt: number) =>
  DateTime.fromMillis(updatedAt)
    .setLocale("ko")
    .toFormat("yyyy. MM. dd. a hh:mm");
